import { useCallback, useRef, useState } from 'react';
import { useBestPractices } from './useBestPractices';
import { AgentType } from '@server/ai';
import { BestPractice } from './types';

import { useSelector } from 'react-redux';
import {
  selectCustomBestPractices,
  selectSweepBestPractices,
} from '../../reducers/bestPracticesReducer';
import { RootState } from '../../reducers';
import { useRunOnceWhenTruthy } from '../common/useRunOnceWhenTruthy';
import { getDomainByAgentType } from './helper';
import { NewBestPracticeCardRef } from './NewBestPracticeCard';
import useConfirm from '../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { ConfirmLeaveWithoutSaveDialog } from '../common/dialogs/ConfirmLeaveWithoutSave/ConfirmLeaveWithoutSaveDialog';
import React from 'react';
import { AiAgent } from '../aiAgentCenter/aiAgentsConsts';
import { getAgentMetadataType } from '../aiAgentCenter/helper';

type TabOption = 'custom' | 'sweep';

export const useBestPracticesContent = (
  agentType?: AgentType,
  agent?: AiAgent | null,
  isAgentCreationContext = false,
) => {
  const [activeTab, setActiveTab] = useState<TabOption>('sweep');
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newCardIsPristine, setNewCardIsPristine] = useState(true);
  const [editableCardPristineStates, setEditableCardPristineStates] = useState<
    Record<string, boolean>
  >({});
  const createNewCardRef = useRef<NewBestPracticeCardRef>(null);
  const applicableDomain = getDomainByAgentType(agentType);
  const { isLoading, fetchBestPractices } = useBestPractices(applicableDomain);
  const { openConfirm } = useConfirm();

  // Filter best practices based on agent's metadataType
  const sweepBestPractices: BestPractice[] | undefined = useSelector((state: RootState) => {
    const allBestPractices = selectSweepBestPractices(state, applicableDomain);
    if (!allBestPractices) return allBestPractices;

    // Get metadataType using type guard pattern
    const agentMetadataType = getAgentMetadataType(agent);

    if (agentMetadataType) {
      // If agent has a specific metadataType, only show best practices that match
      return allBestPractices.filter(
        (bp: BestPractice) => bp.applicableResourceType === getAgentMetadataType(agent),
      );
    }

    return allBestPractices;
  });

  const customBestPractices: BestPractice[] | undefined = useSelector((state: RootState) => {
    const allBestPractices = selectCustomBestPractices(state, applicableDomain);
    if (!allBestPractices) return allBestPractices;

    // Get metadataType using type guard pattern
    const agentMetadataType = getAgentMetadataType(agent);

    if (agentMetadataType) {
      // If agent has a specific metadataType, only show best practices that match
      return allBestPractices.filter(
        (bp: BestPractice) => bp.applicableResourceType === getAgentMetadataType(agent),
      );
    }

    return allBestPractices;
  });

  const dataNotFetched = !sweepBestPractices || !customBestPractices;
  const isCustomEmpty = !dataNotFetched && !isLoading && customBestPractices.length === 0;
  const isSweepEmpty = !dataNotFetched && !isLoading && sweepBestPractices.length === 0;

  useRunOnceWhenTruthy(() => {
    fetchBestPractices();
  }, !!applicableDomain && dataNotFetched);

  // In creation mode, default to Custom tab if no recommended best practices exist (only once)
  useRunOnceWhenTruthy(
    () => {
      setActiveTab('custom');
    },
    isAgentCreationContext && isSweepEmpty && activeTab === 'sweep',
  );

  const closeNewBestPracticeCard = useCallback(() => setIsCreatingNew(false), [setIsCreatingNew]);

  const anyEditableCardDirty = Object.values(editableCardPristineStates).some(
    (isPristine) => !isPristine,
  );
  const isNewCardDirty = !newCardIsPristine && isCreatingNew;
  const isPristine = !anyEditableCardDirty && !isNewCardDirty;

  const handleNewCardIsPristineChange = useCallback((newIsPristine: boolean) => {
    setNewCardIsPristine(newIsPristine);
  }, []);

  const handleEditableCardIsPristineChange = useCallback(
    (cardId: string, newIsPristine: boolean) => {
      setEditableCardPristineStates((prevStates) => ({
        ...prevStates,
        [cardId]: newIsPristine,
      }));
    },
    [],
  );

  const handleCreateNew = useCallback(() => {
    setIsCreatingNew(true);
  }, []);

  const handleTabChange = useCallback(
    async (newTab: TabOption) => {
      if (!isPristine) {
        const isConfirmed = await openConfirm(React.createElement(ConfirmLeaveWithoutSaveDialog));
        if (!isConfirmed) return;
        // Reset all pristine states when user confirms to leave without saving
        setNewCardIsPristine(true);
        setEditableCardPristineStates({});
      }
      // Always reset isCreatingNew when switching tabs
      setIsCreatingNew(false);
      setActiveTab(newTab);
    },
    [isPristine, openConfirm],
  );

  return {
    activeTab,
    setActiveTab: handleTabChange,
    isCreatingNew,
    newCardIsPristine,
    editableCardPristineStates,
    createNewCardRef,
    applicableDomain,
    isLoading,
    sweepBestPractices,
    customBestPractices,
    dataNotFetched,
    isCustomEmpty,
    isSweepEmpty,
    closeNewBestPracticeCard,
    isPristine,
    handleNewCardIsPristineChange,
    handleEditableCardIsPristineChange,
    handleCreateNew,
  };
};
